import {
  JbI18nData
} from "./chunk.3LPJUOJW.js";
import {
  bindLinkHandler,
  showCookieConsentOnPageLoad
} from "./chunk.WTRJMEBX.js";
import {
  jb_cookie_consent_css_default
} from "./chunk.X645CN2X.js";
import {
  ae
} from "./chunk.RIMVGMXQ.js";
import {
  customElement
} from "./chunk.QAGUCU4C.js";
import {
  Initiable
} from "./chunk.6DHNIVN5.js";
import {
  JBCookie,
  mergeDeep
} from "./chunk.VQ52AZAH.js";
import {
  D,
  ke,
  r
} from "./chunk.WKUNRGJV.js";
import {
  n,
  r as r2
} from "./chunk.LWNTGWZJ.js";
import {
  __decorateClass
} from "./chunk.2QRLEY22.js";

// src/components/jb-cookie-consent/jb-cookie-consent.ts
var JbCookieConsent = class extends Initiable(r) {
  constructor() {
    super(...arguments);
    this._open = false;
  }
  static create(data) {
    return new JbCookieConsent().init({ props: data, childNodes: [] });
  }
  close() {
    this._open = false;
  }
  async firstUpdated() {
    window.setTimeout(() => {
      this._prepareCookieSettingsComponent().then();
    }, 50);
    await this.updateComplete;
    dispatchEvent(new CustomEvent("jb-focus-fly:reset", { bubbles: true, composed: true }));
  }
  getCookieSettings() {
    return this._cookieSettingsComponent;
  }
  open() {
    this._open = true;
    window.addEventListener("load", () => {
      this.dispatchEvent(
        new CustomEvent("jb-cookie-consent:open", {
          bubbles: true,
          composed: true
        })
      );
    });
  }
  showCookieSettingsPopup() {
    if (!this._cookieSettingsComponent) {
      return;
    }
    this.dispatchEvent(
      new CustomEvent("jb-popup:transition", {
        bubbles: true,
        composed: true,
        detail: {
          nextChild: this._cookieSettingsComponent
        }
      })
    );
  }
  _getLanguageOptions() {
    const i18nDataComponent = JbI18nData.find();
    if (!i18nDataComponent) {
      return [];
    }
    return i18nDataComponent.getI18nDataLanguageOptionsForCountry(JBCookie.getI18nCookieData().country);
  }
  _getLanguageSelectOptions() {
    if (!this._languageSelectOptions) {
      const i18nDataComponent = JbI18nData.find();
      if (!i18nDataComponent) {
        return [];
      }
      const languageOptions = this._getLanguageOptions();
      this._languageSelectOptions = [];
      languageOptions?.forEach((languageOption) => {
        const language = i18nDataComponent.getLanguageByIsoCode(languageOption.language);
        if (!language) {
          return;
        }
        this._languageSelectOptions.push({
          label: language.shortLabel || language.label,
          value: languageOption.language
        });
      });
    }
    return this._languageSelectOptions;
  }
  _handleButtonClick(button, event) {
    event.preventDefault();
    event.stopPropagation();
    if (button.setsConsentFor) {
      this._cookieSettingsComponent?.setConsentFor(button.setsConsentFor);
    }
    if (button.opensCookieSettings) {
      this.showCookieSettingsPopup();
    } else {
      this._saveLanguageSelection();
      JBCookie.deleteCookie("allow_tracking_cookies");
      JBCookie.deleteCookie("allow_marketing_cookies");
      JBCookie.setCookie("hide_cookie_banner", "1");
      JBCookie.setCookie("show_cookie_banner", "0");
      JBCookie.setCookie("cookie_banner_suppressed", "0");
      this.dispatchEvent(new CustomEvent("jb-popup:close", { bubbles: true, composed: true }));
    }
  }
  _handleFocusOnSection() {
    this.renderRoot.querySelector("jb-dropdown .ts-control")?.focus();
  }
  _handleKeyboardEvent(evt) {
    const target = evt.target;
    switch (evt.code) {
      case "Space":
        if (target.tagName === "JB-RICHTEXT" || target.tagName === "A") {
          evt.preventDefault();
          break;
        }
        break;
      default:
        break;
    }
  }
  _handleLanguageChange(event) {
    event.stopPropagation();
    if (event.detail.newValue && event.detail.newValue !== this._selectedLanguage) {
      this._selectedLanguage = event.detail.newValue;
      this._prepareCookieSettingsComponent().then();
    }
  }
  async _prepareCookieSettingsComponent() {
    const currentLanguage = this._selectedLanguage || JBCookie.getI18nCookieData().languageIso2;
    const effectiveCookieSettings = mergeDeep(
      {},
      this.cookieSettingsPerLanguage.__default,
      this.cookieSettingsPerLanguage[currentLanguage]
    );
    const { JbCookieSettings } = await import("../components/jb-cookie-settings/jb-cookie-settings.js");
    this._cookieSettingsComponent = JbCookieSettings.create(effectiveCookieSettings);
  }
  _saveLanguageSelection() {
    if (!this._selectedLanguage || this._selectedLanguage === JBCookie.getI18nCookieData().languageIso2) {
      return;
    }
    JBCookie.setI18nCookieData({ languageIso2: this._selectedLanguage });
    const selectedOption = this._getLanguageOptions().find(
      (languageOption) => languageOption.language === this._selectedLanguage
    );
    if (!selectedOption) {
      return;
    }
    window.location.href = selectedOption.url;
  }
  render() {
    const languageOptions = this._getLanguageSelectOptions();
    const currentLanguage = this._selectedLanguage || JBCookie.getI18nCookieData().languageIso2 || (languageOptions?.length ? languageOptions[0].value : void 0) || "";
    const content = this.content[currentLanguage] || this.content.__default;
    const headline = this.headlineLabels[currentLanguage] || this.headlineLabels.__default;
    if (!this._open) {
      return ke``;
    }
    if (this.buttons) {
      import("../components/jb-button-panel/jb-button-panel.js").then();
    }
    this.buttons.forEach((button) => {
      if (button.isPrimaryAction) {
        import("../components/jb-button-primary/jb-button-primary.js").then();
      } else {
        import("../components/jb-button-secondary/jb-button-secondary.js").then();
      }
    });
    if (languageOptions && languageOptions.length > 0) {
      import("../components/jb-dropdown/jb-dropdown.js").then();
    }
    return ke`
      <section @keydown=${this._handleKeyboardEvent}>
        <div
          class="container"
          @focus=${this._handleFocusOnSection}
          tabindex="0"
        >
          ${languageOptions && languageOptions.length > 0 ? ke`
                <jb-dropdown
                  @jb-select:changed=${this._handleLanguageChange}
                  inverted
                  .options=${languageOptions}
                  .value=${currentLanguage}
                  showLabel
                  label="${this.languageSelectLabels[currentLanguage] || this.languageSelectLabels.__default}"
                ></jb-dropdown>
              ` : D}
          <div class="text-container">
            <h2>${headline}</h2>
            <jb-richtext .size=${"small"}>${ae(content)}</jb-richtext>
            <jb-button-panel>
              ${this.buttons.map(
      (button) => ke`
                  ${button.isPrimaryAction ? ke`
                        <jb-button-primary
                          @click=${this._handleButtonClick.bind(this, button)}
                          inverted
                        >
                          ${button.labels[currentLanguage] || button.labels.__default}
                        </jb-button-primary>
                      ` : ke`
                        <jb-button-secondary
                          @click=${this._handleButtonClick.bind(this, button)}
                          inverted
                        >
                          ${button.labels[currentLanguage] || button.labels.__default}
                        </jb-button-secondary>
                      `}
                `
    )}
            </jb-button-panel>
          </div>
        </div>
      </section>
    `;
  }
};
JbCookieConsent.styles = jb_cookie_consent_css_default;
JbCookieConsent.shadowRootOptions = { ...r.shadowRootOptions, delegatesFocus: true };
__decorateClass([
  n({ type: Array })
], JbCookieConsent.prototype, "buttons", 2);
__decorateClass([
  n({ type: Object })
], JbCookieConsent.prototype, "content", 2);
__decorateClass([
  n({ type: Object })
], JbCookieConsent.prototype, "cookieSettingsPerLanguage", 2);
__decorateClass([
  n({ type: Object })
], JbCookieConsent.prototype, "headlineLabels", 2);
__decorateClass([
  n({ type: Object })
], JbCookieConsent.prototype, "languageSelectLabels", 2);
__decorateClass([
  n({ type: Boolean })
], JbCookieConsent.prototype, "showOnPageLoad", 2);
__decorateClass([
  r2()
], JbCookieConsent.prototype, "_open", 2);
__decorateClass([
  r2()
], JbCookieConsent.prototype, "_selectedLanguage", 2);
JbCookieConsent = __decorateClass([
  customElement("jb-cookie-consent")
], JbCookieConsent);
bindLinkHandler();
showCookieConsentOnPageLoad();

export {
  JbCookieConsent
};
